/**
 * Advanced In-Memory Cache with Request Deduplication
 */

const cache = new Map();
const pendingRequests = new Map();

/**
 * @param {string} key - Unique key for the cache entry
 * @param {any} value - Data to store
 * @param {number} ttl - Time to live in milliseconds
 */
export const setCache = (key, value, ttl = 5 * 60 * 1000) => {
    const expiresAt = Date.now() + ttl;
    cache.set(key, { value, expiresAt });
};

/**
 * @param {string} key - Cache key to retrieve
 * @returns {any|null} - Cached data or null
 */
export const getCache = (key) => {
    const entry = cache.get(key);
    if (!entry) return null;

    if (Date.now() > entry.expiresAt) {
        cache.delete(key);
        return null;
    }

    return entry.value;
};

/**
 * DEDUPLICATION WRAPPER
 * This is the magic: it prevents multiple simultaneous hits for the same resource.
 * @param {string} key - Unique key for the request
 * @param {Function} fetcher - The actual async API call
 * @param {number} ttl - How long to cache the result
 */
export const fetchWithCache = async (key, fetcher, ttl) => {
    // 1. Check if we already have a valid cached value
    const cachedData = getCache(key);
    if (cachedData) return cachedData;

    // 2. Check if a request for this key is ALREADY in progress
    if (pendingRequests.has(key)) {
        return pendingRequests.get(key);
    }

    // 3. If no cache and no pending, start the request
    const requestPromise = fetcher().then((data) => {
        setCache(key, data, ttl);
        pendingRequests.delete(key); // Cleanup once done
        return data;
    }).catch((err) => {
        pendingRequests.delete(key); // Cleanup on error
        throw err;
    });

    pendingRequests.set(key, requestPromise);
    return requestPromise;
};

export const invalidateCache = (keyPattern) => {
    if (typeof keyPattern === 'string') {
        cache.delete(keyPattern);
    } else if (Array.isArray(keyPattern)) {
        keyPattern.forEach(k => invalidateCache(k));
    } else if (keyPattern instanceof RegExp) {
        for (const key of cache.keys()) {
            if (keyPattern.test(key)) {
                cache.delete(key);
            }
        }
    }
};

export const clearAllCache = () => {
    cache.clear();
    pendingRequests.clear();
};
